import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, Typography, Space, message } from 'antd';
import { InfoCircleOutlined, SendOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const POSRequestForm = ({ open, onClose, provider, providerInfo }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Helper function to handle authentication errors
  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 204 || 
        (error.response?.status === 400)) {
      localStorage.removeItem('auth-token');
      message.error('Session expired. Please log in again.');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const requestData = {
        provider: provider,
        providerName: providerInfo?.name || provider,
        companyName: values.companyName,
        contactEmail: values.contactEmail,
        contactName: values.contactName,
        phoneNumber: values.phoneNumber,
        businessType: values.businessType,
        currentPOSVersion: values.currentPOSVersion,
        useCase: values.useCase,
        timeline: values.timeline,
        additionalInfo: values.additionalInfo,
        requestedAt: new Date().toISOString()
      };

      const response = await axios.post('/workflow/pos-integration-request', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      if (response.data.success) {
        message.success('Integration request submitted successfully! Our team will contact you within 2-3 business days.');
        form.resetFields();
        onClose();
      } else {
        message.error(response.data.error || 'Failed to submit request');
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error submitting POS integration request:', error);
      message.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    'Restaurant',
    'Retail Store', 
    'Food Truck',
    'Bar/Pub',
    'Coffee Shop',
    'Quick Service Restaurant',
    'Fine Dining',
    'Grocery Store',
    'Salon/Spa',
    'Other'
  ];

  const timelineOptions = [
    'Immediate (Within 1 week)',
    'Short term (1-4 weeks)', 
    'Medium term (1-3 months)',
    'Long term (3+ months)',
    'Not sure'
  ];

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <span>Request {providerInfo?.name || provider} Integration</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Paragraph>
          We're working to expand our POS integrations. Please provide your details below and our team will contact you about adding {providerInfo?.name || provider} support to your account.
        </Paragraph>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="companyName"
          label="Company/Business Name"
          rules={[{ required: true, message: 'Please enter your business name' }]}
        >
          <Input placeholder="Enter your business name" />
        </Form.Item>

        <Form.Item
          name="contactName"
          label="Contact Name"
          rules={[{ required: true, message: 'Please enter contact name' }]}
        >
          <Input placeholder="Enter your full name" />
        </Form.Item>

        <Form.Item
          name="contactEmail"
          label="Contact Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="Enter your email address" />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label="Phone Number"
          rules={[{ required: true, message: 'Please enter phone number' }]}
        >
          <Input placeholder="Enter your phone number" />
        </Form.Item>

        <Form.Item
          name="businessType"
          label="Business Type"
          rules={[{ required: true, message: 'Please select business type' }]}
        >
          <Select placeholder="Select your business type">
            {businessTypes.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="currentPOSVersion"
          label={`Current ${providerInfo?.name || provider} Version/Plan`}
        >
          <Input placeholder={`Enter your current ${providerInfo?.name || provider} version or plan`} />
        </Form.Item>

        <Form.Item
          name="useCase"
          label="How do you plan to use this integration?"
          rules={[{ required: true, message: 'Please describe your use case' }]}
        >
          <TextArea 
            rows={15}
            placeholder="Describe how you plan to use this POS integration (e.g., sales reporting, inventory management, customer analytics)" 
          />
        </Form.Item>

        <Form.Item
          name="timeline"
          label="When do you need this integration?"
          rules={[{ required: true, message: 'Please select timeline' }]}
        >
          <Select placeholder="Select your timeline">
            {timelineOptions.map(option => (
              <Option key={option} value={option}>{option}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="additionalInfo"
          label="Additional Information"
        >
          <TextArea 
            rows={2}
            placeholder="Any additional details or specific requirements" 
          />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={loading}
              icon={<SendOutlined />}
            >
              Submit Request
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default POSRequestForm;